import React, {useState} from "react"
import styled from "styled-components"

import {FeedEntityType, GroupCelebration, GroupCelebrationStatus} from "@irati/types"
import authentication from "@irati/auth"
import tracking from "@irati-tracking/recognition/recognition"

import Flex from "@irati/ui/src/Flex"
import {useTheme} from "@irati/ui/src/global/useTheme"
import FormattedMessage from "@irati/utils/lang"
import {stopPropagationAndPrevent} from "@irati-web/wallet/src/utils/stopBubbleEvent"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import RecognitionActions, {ScheduledGroupCelebrationActionsTypes} from "../Popup/ScheduledGroupCelebration/Actions"
import {getGroupCelebrationPath} from "../../utils/paths"
import LikeButton from "../Reactions/LikeButton"
import GroupCelebrationAddReward from "./GroupCelebrationAddReward"
import {Wrapper} from "./styled"
import CommentsIcon from "./Comments"
import EditGreetingScheduledTimeModal from "../Popup/ScheduledGroupCelebration/EditScheduledTime"
import DeleteGroupCelebrationModal from "../Popup/ScheduledGroupCelebration/DeleteGroupCelebration"

const ActionsWrapper = styled.div`
  position: relative;
`
interface Props {
  item: GroupCelebration
  feedItemId?: string
}

const Activity: React.FC<Props> = (props) => {
  const {item} = props
  const [actionMode, setActionMode] = useState<ScheduledGroupCelebrationActionsTypes | null>(null)
  const theme = useTheme()
  const onClickRemove = () => {
    setActionMode(ScheduledGroupCelebrationActionsTypes.DELETE)
    tracking.recognitionCtaClick({
      source: "scheduled_greeting_card",
      type: "delete"
    })
  }
  const onClickEditDate = () => {
    setActionMode(ScheduledGroupCelebrationActionsTypes.EDIT_DATE)
    tracking.recognitionCtaClick({
      source: "scheduled_greeting_card",
      type: "change_schedule"
    })
  }
  const hasUserCommented = item.messages?.some((message) => message.createdBy?.uuid === authentication.user().uuid)
  const isAuthor = authentication.user().uuid === item.author?.uuid
  const messageBtnKey = hasUserCommented
    ? "recognition.groupCelebration.actions.editMessage"
    : "recognition.groupCelebration.actions.addMessage"
  const onMessageClick = () => {
    tracking.recognitionCtaClick({
      source: "scheduled_greeting_card",
      type: hasUserCommented ? "edit_message" : "add_message"
    })
  }
  const inviteOthersClick = () => {
    tracking.recognitionCtaClick({
      source: "scheduled_greeting_card",
      type: "invite_others"
    })
  }
  const onActionClick = (action: ScheduledGroupCelebrationActionsTypes) => {
    switch (action) {
      case ScheduledGroupCelebrationActionsTypes.DELETE:
        return onClickRemove()
      case ScheduledGroupCelebrationActionsTypes.EDIT_DATE:
        return onClickEditDate()
    }
  }

  const onPopupActionClose = () => setActionMode(null)
  return (
    <Flex alignItems="center" wrap="wrap" gap={theme.spacing.oneAndHalf}>
      {item.status === GroupCelebrationStatus.Active && (
        <>
          <Wrapper>
            <FormattedMessage id="recognition.groupCelebration.actions.viewMessages" />
          </Wrapper>
          <CommentsIcon id={item.uuid} item={item} />
        </>
      )}
      {item.status === GroupCelebrationStatus.Active && <LikeButton parent={item} type={FeedEntityType.GroupCelebration} />}
      {item.status === GroupCelebrationStatus.Scheduled && (
        <>
          <ButtonRouterLink
            primary={!hasUserCommented}
            small
            to={`${getGroupCelebrationPath(item)}?edit=${hasUserCommented}`}
            stopPropagation
            onClick={onMessageClick}
            scrollToTop={false}>
            <FormattedMessage id={messageBtnKey} />
          </ButtonRouterLink>
          <ButtonRouterLink
            onClick={inviteOthersClick}
            scrollToTop={false}
            to={`${getGroupCelebrationPath(item)}/invite`}
            small
            stopPropagation>
            <FormattedMessage id="recognition.groupCelebration.actions.inviteOthers" />
          </ButtonRouterLink>
        </>
      )}
      {isAuthor && <GroupCelebrationAddReward item={item} />}
      {((isAuthor && item.status === GroupCelebrationStatus.Scheduled) ||
        (item.status === GroupCelebrationStatus.Active && authentication.isGlobalAdmin())) && (
        <ActionsWrapper onClick={stopPropagationAndPrevent}>
          <RecognitionActions groupCelebration={item} onActionClick={onActionClick} />
          <DeleteGroupCelebrationModal
            onClose={onPopupActionClose}
            onDone={onPopupActionClose}
            show={actionMode === ScheduledGroupCelebrationActionsTypes.DELETE}
            groupCelebration={item}
          />
          <EditGreetingScheduledTimeModal
            onClose={onPopupActionClose}
            onDone={onPopupActionClose}
            show={actionMode === ScheduledGroupCelebrationActionsTypes.EDIT_DATE}
            groupCelebration={item}
          />
        </ActionsWrapper>
      )}
    </Flex>
  )
}

export default Activity
